/**
 * ActionItemsBlockView — Phase 6 free-flow rewrite.
 *
 * Container for editable actionItem children. NodeViewContent
 * renders them; an "+ Add action item" button below the list
 * dispatches an insert via the editor command chain.
 */
import {
  NodeViewContent,
  NodeViewWrapper,
  type NodeViewProps,
} from '@tiptap/react';
import { Plus } from 'lucide-react';

export function ActionItemsBlockView({ node, editor, getPos }: NodeViewProps) {
  const isEditable = editor.isEditable;
  const hasItems = node.childCount > 0;

  if (!isEditable && !hasItems) return null;

  const addItem = () => {
    const pos = typeof getPos === 'function' ? getPos() : null;
    if (pos === null) return;
    const endOfBlock = pos + node.nodeSize - 1;
    editor
      .chain()
      .focus()
      .insertContentAt(endOfBlock, {
        type: 'actionItem',
        attrs: { status: 'open' },
        content: [],
      })
      .run();
  };

  return (
    <NodeViewWrapper
      className="tt-action-items"
      data-node="actionItemsBlock"
    >
      {(isEditable || hasItems) && (
        <h3
          contentEditable={false}
          className="mt-6 mb-3 select-none border-b border-smoke/30 pb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-pine"
        >
          Action Items
        </h3>
      )}

      <NodeViewContent
        as="ul"
        className="tt-action-items__list space-y-1"
      />

      {isEditable && (
        <div contentEditable={false} className="mt-1.5">
          <button
            type="button"
            onClick={addItem}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-pine/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em] text-pine transition-colors hover:bg-pine/10"
          >
            <Plus size={11} strokeWidth={2.5} />
            Add action item
          </button>
        </div>
      )}
    </NodeViewWrapper>
  );
}
